import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Clapperboard, Play } from "lucide-react";
import { api } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Cover } from "@/components/Cover";
import { fmtLong } from "@/pages/Audiobooks";
import { VideoThumb, epCode, fmtClock } from "@/components/VideoBits";

const useContinue = () => {
  const p = usePlayer();
  const [book, setBook] = useState(null);
  const [video, setVideo] = useState(null);
  useEffect(() => { api.get("/audiobooks/continue").then((r) => setBook(r.data.book)).catch(() => setBook(null)); }, [p.current?.album_id]);
  useEffect(() => { api.get("/videos/continue").then((r) => setVideo(r.data.video)).catch(() => setVideo(null)); }, []);
  return { book, video };
};

const chip = "flex h-10 min-w-0 max-w-[320px] flex-1 items-center gap-2 rounded-full border border-zinc-300/80 bg-white/80 py-1 pl-1 pr-1 shadow-sm backdrop-blur";
const resumeBtn = "flex h-8 shrink-0 items-center gap-1 rounded-full bg-zinc-900 px-3 text-[11px] font-medium text-white transition-transform hover:scale-[1.04] active:scale-95";

const BookChip = ({ book }) => {
  const p = usePlayer();
  const pr = book.progress;
  const left = Math.max(0, (pr.total_duration || 0) - (pr.elapsed || 0));
  const isCurrent = p.current?.album_id === book.id && p.current?.is_audiobook;
  const resume = () => {
    if (isCurrent) { p.toggle(); return; }
    if (p.shuffle) p.setShuffle(false);
    const idx = Math.max(0, book.tracks.findIndex((t) => t.id === pr.track_id));
    p.playTracks(book.tracks, idx, pr.position || 0);
  };
  return (
    <div className={chip} data-testid="continue-book" title={`${book.name} — ${book.artist}`}>
      <Link to={`/audiobooks/${book.id}`} className="shrink-0" data-testid="continue-listening-cover"><Cover albumId={book.id} hasCover={book.has_cover} version={book.cover_version} className="h-8 w-8" rounded="rounded-full" iconSize={12} /></Link>
      <Link to={`/audiobooks/${book.id}`} className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-zinc-400"><BookOpen size={9} className="mr-1 inline-block align-[-1px]" />Audiobook · {Math.round(pr.pct)}% · {fmtLong(left)} left</span>
        <span className="block truncate text-xs font-medium text-zinc-900" data-testid="continue-listening-title">{book.name}</span>
      </Link>
      <button onClick={resume} className={resumeBtn} data-testid="continue-listening-resume"><Play size={11} fill="currentColor" />{isCurrent && p.playing ? "Pause" : "Resume"}</button>
    </div>
  );
};

const VideoChip = ({ v }) => {
  const nav = useNavigate();
  const pr = v.progress;
  const left = Math.max(0, (v.duration || 0) - (pr.position || 0));
  return (
    <div className={chip} data-testid="continue-video" title={v.title}>
      <Link to={`/videos/watch/${v.id}`} className="w-14 shrink-0" data-testid="continue-video-thumb"><VideoThumb id={v.id} hasThumb={v.has_thumb} className="!h-8 !w-14 !rounded-full" /></Link>
      <Link to={`/videos/watch/${v.id}`} className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-zinc-400"><Clapperboard size={9} className="mr-1 inline-block align-[-1px]" />Video{epCode(v) ? ` · ${epCode(v)}` : ""} · {Math.round(pr.pct)}%{v.duration ? ` · ${fmtClock(left)} left` : ""}</span>
        <span className="block truncate text-xs font-medium text-zinc-900" data-testid="continue-video-title">{v.title}</span>
      </Link>
      <button onClick={() => nav(`/videos/watch/${v.id}`)} className={resumeBtn} data-testid="continue-video-resume"><Play size={11} fill="currentColor" />Resume</button>
    </div>
  );
};

/* Fixed-height "continue" chips for the top bar; only rendered on the Recently Added page. */
export const ContinueChips = () => {
  const { book, video } = useContinue();
  if (!book && !video) return null;
  return (
    <div className="flex min-w-0 flex-1 items-center justify-center gap-2 overflow-hidden" data-testid="continue-listening">
      {book && <BookChip book={book} />}
      {video && <VideoChip v={video} />}
    </div>
  );
};
